import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/page-header";
import { usePermissions } from "@/hooks/usePermissions";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, Search } from "lucide-react";
import { PricingComponentTable } from "@/components/pricing-components/PricingComponentTable";
import { PricingComponentForm } from "@/components/pricing-components/PricingComponentForm";
import {
  usePricingComponents,
  usePricingComponent,
} from "@/hooks/usePricingComponents";
import {
  COMPONENT_TYPES,
  type ComponentType,
  type PricingComponentListDto,
} from "@/types/pricing-component";

// ============================================================================
// Component
// ============================================================================

export function PricingComponentListPage() {
  // Catalog writes are SuperAdmin+BillingAdmin; the listing itself reads fine for all.
  const { hasRole } = usePermissions();
  const canEditCatalog = hasRole(["SuperAdmin", "BillingAdmin"]);
  const navigate = useNavigate();

  // Filter states
  const [search, setSearch] = useState("");
  const [componentType, setComponentType] = useState<ComponentType | "All">(
    "All",
  );
  const [isActive, setIsActive] = useState<"All" | "Active" | "Inactive">(
    "All",
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editComponentId, setEditComponentId] = useState<string | null>(null);

  // Fetch full detail for edit mode
  const { data: editComponentDetail } = usePricingComponent(
    isEditModalOpen ? editComponentId : undefined,
  );

  // Build filter object
  const filters = {
    search: search || undefined,
    componentType: componentType === "All" ? undefined : componentType,
    isActive:
      isActive === "All" ? undefined : isActive === "Active" ? true : false,
    page: page,
    limit: pageSize,
  };

  // Fetch components
  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = usePricingComponents(filters);

  const components = response?.data || [];
  const totalPages = response?.pagination?.totalPages || 1;
  const currentPage = response?.pagination?.currentPage || 1;
  const totalCount = response?.pagination?.totalItems || 0;

  const startItem =
    components.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  // Event handlers
  const handleEdit = (component: PricingComponentListDto) => {
    setEditComponentId(component.id);
    setIsEditModalOpen(true);
  };

  const handleViewPrices = (component: PricingComponentListDto) => {
    // Navigate to component detail page with prices
    navigate(`/admin/pricing/components/${component.id}`);
  };

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Pricing Components"
          subtitle="Manage pricing component building blocks"
        />
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-destructive-ink">
                Failed to load pricing components
              </p>
              <Button onClick={() => refetch()} className="mt-4">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pricing Components"
        subtitle="Manage pricing component building blocks"
        actions={!canEditCatalog ? [] : [
          {
            label: "Create Component",
            onClick: () => setIsCreateModalOpen(true),
            icon: <Plus className="mr-2 h-4 w-4" />,
          },
        ]}
      />

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>
            Search and filter pricing components
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 md:flex-row">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, slug, or unit..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1); // Reset to first page on search
                }}
                className="pl-10"
              />
            </div>

            {/* Component Type Filter */}
            <Select
              value={componentType}
              onValueChange={(value) => {
                setComponentType(value as ComponentType | "All");
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Component Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Types</SelectItem>
                {COMPONENT_TYPES.map((ct) => (
                  <SelectItem key={ct} value={ct}>
                    {ct}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select
              value={isActive}
              onValueChange={(value) => {
                setIsActive(value as "All" | "Active" | "Inactive");
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Statuses</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Components Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Components ({totalCount})</CardTitle>
              {totalCount > 0 && (
                <CardDescription>
                  Showing {startItem}-{endItem} of {totalCount} components
                </CardDescription>
              )}
            </div>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => {
                setPageSize(parseInt(value));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 per page</SelectItem>
                <SelectItem value="20">20 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">
                Loading pricing components...
              </p>
            </div>
          ) : (
            <>
              <PricingComponentTable
                components={components}
                isLoading={isLoading}
                onEdit={handleEdit}
                onViewPrices={handleViewPrices}
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Component Modal */}
      <PricingComponentForm
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        mode="create"
      />

      {/* Edit Component Modal */}
      <PricingComponentForm
        open={isEditModalOpen}
        onOpenChange={(open) => {
          setIsEditModalOpen(open);
          if (!open) setEditComponentId(null);
        }}
        component={editComponentDetail}
        mode="edit"
      />
    </div>
  );
}
